/* eslint-disable @typescript-eslint/no-explicit-any */
type OptionDefinition<T> =
    | {
          alias?: string;
          name: string;
          defaultValue: T;
          parse: (input: string) => T;
          multiple?: false;
          defaultOption?: boolean;
          doesNotRequireValue?: false;
          description: string;
      }
    | {
          alias?: string;
          name: string;
          defaultValue: T;
          parse: (input: string[]) => T;
          multiple: true;
          defaultOption: boolean;
          doesNotRequireValue?: false;
          description: string;
      }
    | {
          alias?: string;
          name: string;
          defaultValue: T;
          parse: (input: boolean) => T;
          multiple?: false;
          defaultOption?: false;
          doesNotRequireValue: true;
          description: string;
      };

export type OptionsDefinition<T> = Array<OptionDefinition<any>> & { __Opaque__: T };

export type CommandsDefinition<T> = Array<CommandDefinition<any, any>> & { __OPAQUE__: T };

interface CommandDefinition<T, _O> {
    name: string;
    typeValue: T;
    options: OptionsDefinition<T>;
    description?: string;
    detailedDescription?: string;
    examples?: string;
    extendedDetails?: string;
}

type OptionsDefinitionList<TArgs extends any[]> = { [P in keyof TArgs]: OptionDefinition<TArgs[P]> };

type And<TArgs extends any[]> = TArgs extends []
    ? object
    : TArgs extends [infer T1]
      ? T1
      : TArgs extends [infer T1, infer T2]
        ? T1 & T2
        : TArgs extends [infer T1, infer T2, infer T3]
          ? T1 & T2 & T3
          : TArgs extends [infer T1, infer T2, infer T3, infer T4]
            ? T1 & T2 & T3 & T4
            : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5]
              ? T1 & T2 & T3 & T4 & T5
              : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5, infer T6]
                ? T1 & T2 & T3 & T4 & T5 & T6
                : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5, infer T6, infer T7]
                  ? T1 & T2 & T3 & T4 & T5 & T6 & T7
                  : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5, infer T6, infer T7, infer T8]
                    ? T1 & T2 & T3 & T4 & T5 & T6 & T7 & T8
                    : TArgs extends [
                            infer T1,
                            infer T2,
                            infer T3,
                            infer T4,
                            infer T5,
                            infer T6,
                            infer T7,
                            infer T8,
                            infer T9,
                        ]
                      ? T1 & T2 & T3 & T4 & T5 & T6 & T7 & T8 & T9
                      : never;

type Or<TArgs extends any[]> = TArgs extends []
    ? object
    : TArgs extends [infer T1]
      ? T1
      : TArgs extends [infer T1, infer T2]
        ? T1 | T2
        : TArgs extends [infer T1, infer T2, infer T3]
          ? T1 | T2 | T3
          : TArgs extends [infer T1, infer T2, infer T3, infer T4]
            ? T1 | T2 | T3 | T4
            : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5]
              ? T1 | T2 | T3 | T4 | T5
              : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5, infer T6]
                ? T1 | T2 | T3 | T4 | T5 | T6
                : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5, infer T6, infer T7]
                  ? T1 | T2 | T3 | T4 | T5 | T6 | T7
                  : TArgs extends [infer T1, infer T2, infer T3, infer T4, infer T5, infer T6, infer T7, infer T8]
                    ? T1 | T2 | T3 | T4 | T5 | T6 | T7 | T8
                    : TArgs extends [
                            infer T1,
                            infer T2,
                            infer T3,
                            infer T4,
                            infer T5,
                            infer T6,
                            infer T7,
                            infer T8,
                            infer T9,
                        ]
                      ? T1 | T2 | T3 | T4 | T5 | T6 | T7 | T8 | T9
                      : never;

export function optionsDefinition<TArgs extends any[]>(
    ...args: OptionsDefinitionList<TArgs>
): OptionsDefinition<And<TArgs>>;

interface CommandDefinitionOptions {
    description?: string;
    detailedDescription?: string;
    examples?: string;
    extendedDetails?: string;
}

export declare function commandDefinition<T, O>(
    name: string,
    type: T,
    def: OptionsDefinition<O>,
    options?: CommandDefinitionOptions
): CommandDefinition<{ type: T }, O>;

export declare function commandsDefinition<T1, O1>(
    c1: CommandDefinition<T1, O1>
): CommandsDefinition<T1 & { options: O1 }>;
export declare function commandsDefinition<T1, O1, T2, O2>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>
): CommandsDefinition<(T1 & { options: O1 }) | (T2 & { options: O2 })>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>
): CommandsDefinition<(T1 & { options: O1 }) | (T2 & { options: O2 }) | (T3 & { options: O3 })>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3, T4, O4>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>
): CommandsDefinition<
    (T1 & { options: O1 }) | (T2 & { options: O2 }) | (T3 & { options: O3 }) | (T4 & { options: O4 })
>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3, T4, O4, T5, O5>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3, T4, O4, T5, O5, T6, O6>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>,
    c6: CommandDefinition<T6, O6>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
    | (T6 & { options: O6 })
>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3, T4, O4, T5, O5, T6, O6, T7, O7>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>,
    c6: CommandDefinition<T6, O6>,
    c7: CommandDefinition<T7, O7>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
    | (T6 & { options: O6 })
    | (T7 & { options: O7 })
>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3, T4, O4, T5, O5, T6, O6, T7, O7, T8, O8>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>,
    c6: CommandDefinition<T6, O6>,
    c7: CommandDefinition<T7, O7>,
    c8: CommandDefinition<T8, O8>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
    | (T6 & { options: O6 })
    | (T7 & { options: O7 })
    | (T8 & { options: O8 })
>;
export declare function commandsDefinition<T1, O1, T2, O2, T3, O3, T4, O4, T5, O5, T6, O6, T7, O7, T8, O8, T9, O9>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>,
    c6: CommandDefinition<T6, O6>,
    c7: CommandDefinition<T7, O7>,
    c8: CommandDefinition<T8, O8>,
    c9: CommandDefinition<T9, O9>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
    | (T6 & { options: O6 })
    | (T7 & { options: O7 })
    | (T8 & { options: O8 })
    | (T9 & { options: O9 })
>;
export declare function commandsDefinition<
    T1,
    O1,
    T2,
    O2,
    T3,
    O3,
    T4,
    O4,
    T5,
    O5,
    T6,
    O6,
    T7,
    O7,
    T8,
    O8,
    T9,
    O9,
    T10,
    O10,
>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>,
    c6: CommandDefinition<T6, O6>,
    c7: CommandDefinition<T7, O7>,
    c8: CommandDefinition<T8, O8>,
    c8: CommandDefinition<T9, O9>,
    c9: CommandDefinition<T10, O10>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
    | (T6 & { options: O6 })
    | (T7 & { options: O7 })
    | (T8 & { options: O8 })
    | (T9 & { options: O9 })
    | (T10 & { options: O10 })
>;
export declare function commandsDefinition<
    T1,
    O1,
    T2,
    O2,
    T3,
    O3,
    T4,
    O4,
    T5,
    O5,
    T6,
    O6,
    T7,
    O7,
    T8,
    O8,
    T9,
    O9,
    T10,
    O10,
    T11,
    O11,
>(
    c1: CommandDefinition<T1, O1>,
    c2: CommandDefinition<T2, O2>,
    c3: CommandDefinition<T3, O3>,
    c4: CommandDefinition<T4, O4>,
    c5: CommandDefinition<T5, O5>,
    c6: CommandDefinition<T6, O6>,
    c7: CommandDefinition<T7, O7>,
    c8: CommandDefinition<T8, O8>,
    c8: CommandDefinition<T9, O9>,
    c9: CommandDefinition<T10, O10>,
    c10: CommandDefinition<T11, O11>
): CommandsDefinition<
    | (T1 & { options: O1 })
    | (T2 & { options: O2 })
    | (T3 & { options: O3 })
    | (T4 & { options: O4 })
    | (T5 & { options: O5 })
    | (T6 & { options: O6 })
    | (T7 & { options: O7 })
    | (T8 & { options: O8 })
    | (T9 & { options: O9 })
    | (T10 & { options: O10 })
    | (T11 & { options: O11 })
>;

export declare function buildCommandBasedOptionsParser<T>(
    def: CommandsDefinition<T>,
    options: BuildCommandBasedOptionsParserConfig
): CommandLineArgsParser<T>;

interface BuildCommandBasedOptionsParserConfig {
    commandName: string;
    commandDescription: string;
    usageString?: string;
}

interface CommandLineArgsParser<T> {
    (input: string[]): T;
    getCommandListUsage(): stirng;
    getCommandUsage(type: T.type): stirng;
}

export declare class InvalidCommandLineArgumentsError extends Error {
    public readonly usageText: string;
    public constructor(message: string, usageText: string);
}

export declare class CommandLineArgumentsHelpRequestedError extends Error {
    public readonly usageText: string;
    public constructor(usageText: string);
}
